import { oneTimesUpdate } from "@domain/coupons"
import LoadingButton from "@mui/lab/LoadingButton"
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material"
import type { IAiPrompts } from "@pages/main/scripts/prompts"
import {
  AiPromptUsage,
  formatModelPrice,
  GeminiModels,
  GPTModels,
  isGeminiModel,
  isGeminiOnlyPrompt,
  promptLayerLabel,
  promptRoleLabel,
  promptRowKey,
  type AiModels
} from "@pages/main/scripts/prompts/prompt.models"
import { showSuccessToast } from "@utils/toast"
import { OneTimesType } from "__generated__/graphql"
import { useEffect, useMemo, useState } from "react"

const KEEP = ""

type Patch = { aiModel?: AiModels; usage?: AiPromptUsage }

type PlannedRow = {
  prompt: IAiPrompts
  patch: Patch
  blocked: string | null
}

type Props = {
  open: boolean
  onClose: () => void
  prompts: IAiPrompts[]
  onApplied: () => Promise<unknown>
}

function planRow(
  prompt: IAiPrompts,
  aiModel: AiModels | typeof KEEP,
  usage: AiPromptUsage | typeof KEEP
): PlannedRow {
  const patch: Patch = {}
  if (aiModel && aiModel !== prompt.aiModel) patch.aiModel = aiModel
  if (usage && usage !== prompt.usage) patch.usage = usage

  const blocked =
    patch.aiModel &&
    isGeminiOnlyPrompt(prompt.name) &&
    !isGeminiModel(patch.aiModel)
      ? "Gemini only"
      : null

  return { prompt, patch, blocked }
}

export default function PromptBulkEditDialog({
  open,
  onClose,
  prompts,
  onApplied
}: Props) {
  const [aiModel, setAiModel] = useState<AiModels | typeof KEEP>(KEEP)
  const [usage, setUsage] = useState<AiPromptUsage | typeof KEEP>(KEEP)
  const [applying, setApplying] = useState(false)
  const [done, setDone] = useState(0)
  const [failures, setFailures] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open) return
    setAiModel(KEEP)
    setUsage(KEEP)
    setDone(0)
    setFailures({})
  }, [open])

  const plan = useMemo(
    () =>
      prompts
        .map((p) => planRow(p, aiModel, usage))
        .sort((a, b) => a.prompt.name.localeCompare(b.prompt.name)),
    [prompts, aiModel, usage]
  )

  const toApply = plan.filter(
    (row) => !row.blocked && Object.keys(row.patch).length > 0
  )
  const blockedCount = plan.filter((row) => row.blocked).length
  const baseCount = toApply.filter((row) => !row.prompt.engineVersion).length

  async function apply() {
    const confirmed = window.confirm(
      `${toApply.length} prompt satırı güncellenecek. Devam edilsin mi?`
    )
    if (!confirmed) return

    setApplying(true)
    setDone(0)
    setFailures({})
    const failed: Record<string, string> = {}

    for (const row of toApply) {
      const { isSuccess, errorMessage } = await oneTimesUpdate({
        type: OneTimesType.UpdateAiPrompt,
        metaData: {
          name: row.prompt.name,
          role: row.prompt.role ?? null,
          engineVersion: row.prompt.engineVersion ?? null,
          ...row.patch
        }
      })
      if (!isSuccess) {
        failed[promptRowKey(row.prompt)] = String(errorMessage)
      }
      setDone((n) => n + 1)
    }

    await onApplied()
    setApplying(false)
    setFailures(failed)

    const failedCount = Object.keys(failed).length
    if (failedCount === 0) {
      showSuccessToast(`${toApply.length} prompt updated`)
      onClose()
    }
  }

  const failedCount = Object.keys(failures).length

  return (
    <Dialog
      open={open}
      onClose={applying ? undefined : onClose}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>Bulk Edit ({prompts.length} prompt)</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 320 }}>
              <InputLabel>AI Model</InputLabel>
              <Select
                label="AI Model"
                value={aiModel}
                disabled={applying}
                onChange={(e) => setAiModel(e.target.value as AiModels)}
              >
                <MenuItem value={KEEP}>
                  <em>Don&apos;t change</em>
                </MenuItem>
                <ListSubheader>ChatGPT Models</ListSubheader>
                {Object.values(GPTModels).map((m) => (
                  <MenuItem key={m} value={m}>
                    🤖 {formatModelPrice(m)}
                  </MenuItem>
                ))}
                <ListSubheader>Gemini Models</ListSubheader>
                {Object.values(GeminiModels).map((m) => (
                  <MenuItem key={m} value={m}>
                    💎 {formatModelPrice(m)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Usage</InputLabel>
              <Select
                label="Usage"
                value={usage}
                disabled={applying}
                onChange={(e) => setUsage(e.target.value as AiPromptUsage)}
              >
                <MenuItem value={KEEP}>
                  <em>Don&apos;t change</em>
                </MenuItem>
                {Object.values(AiPromptUsage).map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {baseCount > 0 && (
            <Alert severity="warning">
              {baseCount} BASE row(s) will change — every engine version without
              its own copy of these prompts reads them.
            </Alert>
          )}
          {blockedCount > 0 && (
            <Alert severity="info">
              {blockedCount} row(s) skipped: they only run on Gemini models.
            </Alert>
          )}
          {failedCount > 0 && (
            <Alert severity="error">
              {failedCount} row(s) failed — see the table below. The rest were
              saved.
            </Alert>
          )}

          {applying && (
            <Box>
              <LinearProgress
                variant="determinate"
                value={toApply.length ? (done / toApply.length) * 100 : 0}
              />
              <Typography variant="caption" color="text.secondary">
                {done} / {toApply.length}
              </Typography>
            </Box>
          )}

          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Layer</TableCell>
                <TableCell>AI Model</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plan.map((row) => {
                const key = promptRowKey(row.prompt)
                return (
                  <TableRow key={key}>
                    <TableCell sx={{ fontSize: "0.8rem" }}>
                      {row.prompt.name}
                    </TableCell>
                    <TableCell>{promptRoleLabel(row.prompt.role)}</TableCell>
                    <TableCell>
                      {promptLayerLabel(row.prompt.engineVersion)}
                    </TableCell>
                    <TableCell>
                      <ChangeCell
                        from={row.prompt.aiModel}
                        to={row.patch.aiModel}
                      />
                    </TableCell>
                    <TableCell>
                      <ChangeCell
                        from={row.prompt.usage}
                        to={row.patch.usage}
                      />
                    </TableCell>
                    <TableCell>
                      <RowStatus row={row} failure={failures[key]} />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={applying}>
          Close
        </Button>
        <LoadingButton
          variant="contained"
          loading={applying}
          disabled={applying || toApply.length === 0}
          onClick={apply}
        >
          Apply to {toApply.length} row(s)
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

function ChangeCell({ from, to }: { from?: string; to?: string }) {
  if (!to) {
    return (
      <Typography variant="body2" color="text.secondary">
        {from ?? "—"}
      </Typography>
    )
  }
  return (
    <Typography variant="body2">
      <Box component="span" sx={{ textDecoration: "line-through", mr: 0.5 }}>
        {from ?? "—"}
      </Box>
      → <b>{to}</b>
    </Typography>
  )
}

function RowStatus({ row, failure }: { row: PlannedRow; failure?: string }) {
  if (failure) {
    return <Chip size="small" color="error" label={failure} />
  }
  if (row.blocked) {
    return <Chip size="small" label={row.blocked} />
  }
  if (Object.keys(row.patch).length === 0) {
    return <Chip size="small" variant="outlined" label="No change" />
  }
  return <Chip size="small" color="primary" label="Will change" />
}
